import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DriveItemDisplayType, FileType } from "@jugl-web/rest-api/drive/types";

export interface UploadingFileItem {
  id: string;
  dirId: string;
  file: File;
  progress: number;
  isFailed?: boolean;
  isCompleted?: boolean;
  isCancelled?: boolean;
}

type DriveState = {
  displayType: {
    [key: string]: DriveItemDisplayType;
  };
  objectAction: {
    id?: string;
    type?: FileType;
    name?: string;
    allowed_users?: { id: string; img: string; name: string }[];
    action?: "edit" | "rename";
  };
  visited: { id: string; name: string }[];
  uploadingFiles: UploadingFileItem[];
  filesUploadProgress: {
    [key: string]: {
      isFailed?: boolean;
      isCompleted?: boolean;
      isCancelled?: boolean;
      progress: number;
    };
  };
  activeUploads: {
    [key: string]: boolean;
  };
};

const initialState: DriveState = {
  displayType: {},
  objectAction: {
    id: undefined,
    type: undefined,
    name: undefined,
    allowed_users: undefined,
    action: undefined,
  },
  visited: [],
  uploadingFiles: [],
  filesUploadProgress: {},
  activeUploads: {},
};

const driveSlice = createSlice({
  name: "drive",
  initialState,
  reducers: {
    change(
      state,
      action: PayloadAction<[id: string, display: DriveItemDisplayType]>
    ) {
      const [id, display] = action.payload;
      state.displayType[id] = display;
    },
    actionSidebar(state, action: PayloadAction<DriveState["objectAction"]>) {
      state.objectAction = action.payload;
    },
    setVisited(state, action: PayloadAction<{ id: string; name: string }[]>) {
      const visited = action.payload;
      state.visited = visited;
    },
    addUploadingFiles(state, action: PayloadAction<UploadingFileItem[]>) {
      state.uploadingFiles.push(...action.payload);
    },
    updateUploadProgress(
      state,
      action: PayloadAction<{
        progressId: string;
        progress: Partial<DriveState["filesUploadProgress"][string]>;
      }>
    ) {
      const { progressId, progress } = action.payload;
      state.filesUploadProgress[progressId] = {
        ...state.filesUploadProgress[progressId],
        ...progress,
      };
    },
    updateUploadingFile(
      state,
      action: PayloadAction<{
        fileId: string;
        updates: Partial<UploadingFileItem>;
      }>
    ) {
      const { fileId, updates } = action.payload;
      const fileIndex = state.uploadingFiles.findIndex(
        (file) => file.id === fileId
      );
      if (fileIndex !== -1) {
        state.uploadingFiles[fileIndex] = {
          ...state.uploadingFiles[fileIndex],
          ...updates,
        };
      }
    },
    removeUploadingFile(state, action: PayloadAction<string>) {
      const fileId = action.payload;
      state.uploadingFiles = state.uploadingFiles.filter(
        (file) => file.id !== fileId
      );
    },
    removeCompletedFiles(state) {
      const completedFiles = state.uploadingFiles.filter(
        (file) => file.isCompleted
      );

      completedFiles.forEach((file) => {
        const progressId = `${file.dirId}-${file.id}`;
        delete state.filesUploadProgress[progressId];
      });

      state.uploadingFiles = state.uploadingFiles.filter(
        (file) => !file.isCompleted
      );
    },
    removeCancelledFiles(state) {
      const cancelledFiles = state.uploadingFiles.filter(
        (file) => file.isCancelled
      );

      cancelledFiles.forEach((file) => {
        const progressId = `${file.dirId}-${file.id}`;
        delete state.filesUploadProgress[progressId];
      });

      state.uploadingFiles = state.uploadingFiles.filter(
        (file) => !file.isCancelled
      );
    },
    setActiveUpload(
      state,
      action: PayloadAction<{ fileId: string; active: boolean }>
    ) {
      const { fileId, active } = action.payload;
      if (active) {
        state.activeUploads[fileId] = true;
      } else {
        delete state.activeUploads[fileId];
      }
    },
    clearUploadProgress(state, action: PayloadAction<string>) {
      const progressId = action.payload;
      delete state.filesUploadProgress[progressId];
    },
  },
});

export const {
  change,
  actionSidebar,
  setVisited,
  addUploadingFiles,
  updateUploadProgress,
  updateUploadingFile,
  removeUploadingFile,
  removeCompletedFiles,
  removeCancelledFiles,
  setActiveUpload,
  clearUploadProgress,
} = driveSlice.actions;
export default driveSlice;
